/**
 * ImportFromConnectorModalV2 — wire skills from an installed connector
 * onto an agent (G171).
 *
 * Replaces the previously-dead "Import from connector" button on the agent
 * Build tab. Lists every connector this tenant has installed, shows the
 * connector's operations (manifest.operations / manifest.skills), and lets
 * the operator pick one or more to materialize as Skill rows on this agent.
 *
 * For each selected skill the modal:
 *   1. POSTs /api/v1/agents/{agentId}/connector-assignments  (idempotent — 409 is fine)
 *   2. POSTs /api/v1/agents/{agentId}/skills                  (creates the Skill row)
 *   3. POSTs /api/v1/agents/{agentId}/skills/{skillId}/connectors (links skill → connector)
 *
 * The created Skill carries the operation's `endpoint`, `method`, `params`,
 * and `baseUrl` so the existing ConnectorExecutionService dispatch path
 * (which honors config.endpoint + skillParams) executes correctly without
 * any extra wiring.
 */
import React, { useEffect, useMemo, useState } from 'react';
// Phase 5B — Modal → Slideout per G378. Skill-import is a two-column
// picker (connectors list + operations checkboxes, ~50+ potential
// rows depending on connector). Wider slideout fits the dual layout.
import { Slideout, Button, Pill } from './index';
import { CheckCircle2, AlertCircle, Plug, Search } from 'lucide-react';
import {
  apiFetch,
  generateEval,
  getAssignedConnectorIds,
} from '../../services/api';
// V1 pattern restored 2026-05-29: connector list comes from PlatformContext
// (which owns the /api/v1/connectors fetch + the V227 ConnectorResponse
// wrapper flatten), NOT a direct api.getConnectors call. The earlier V2
// redesign regressed by fetching here directly and rendering wrapper rows
// with no top-level id/name/type — operators saw an empty list.
import { usePlatform } from '../../contexts/PlatformContext';
import { findMarketplaceForInstalled } from '../../data/marketplaceFromRegistry';
import { useMarketplaceCatalogue } from '../../data/marketplaceRegistry';

export function ImportFromConnectorModalV2({
  isOpen,
  agentId,
  agentName,
  onClose,
  onImported,
}) {
  /* G383 — V1↔V2 parity fix. The skill catalogue MUST come from the same
   * marketplace-shaped source V1 used (useMarketplaceCatalogue → connectors
   * mapped via manifestToMarketplaceConnector). Previously this modal passed
   * RAW listConnectorManifests() output into findMarketplaceForInstalled(),
   * which matches on the *mapped* shape (manifestId / legacyIds / top-level
   * baseUrl) — so matching was fragile ("No manifest found…") and even on a
   * hit the op rows lacked requiredScopes / samplePayload / mapped params. */
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();
  /* V1 pattern: connectors come from PlatformContext. PC owns the fetch
   * + the V227 wrapper flatten, so every row here already has
   * `id`/`name`/`type` at the top level. */
  const { connectors = [], loadConnectors } = usePlatform();
  const loading = false;             /* PC-backed; loading state is per-page, not per-modal */
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);
  const [selectedSkillIds, setSelectedSkillIds] = useState(() => new Set());
  const [importing, setImporting]   = useState(false);
  const [progress, setProgress]     = useState(null);
  const [imported, setImported]     = useState([]);     // {skillName, ok, error?}
  /* DEF-LLC-IMPORT-RBAC-001 — connector ids ASSIGNED to this agent. The
   * importable connector list MUST be scoped to these (same rule the inline
   * Connector-skill slideout enforces). Listing all tenant-installed
   * connectors here surfaced connectors the agent has no grant for. */
  const [assignedConnectorIds, setAssignedConnectorIds] = useState([]);

  /* On open, reset transient state + load the agent's assigned connector ids.
   * If the PC connector list is empty (operator deep-linked here before
   * visiting the Connectors page), kick off PC's loader too. */
  useEffect(() => {
    if (!isOpen) return undefined;
    setError(null);
    setSelectedConnectorId(null);
    setSelectedSkillIds(new Set());
    setImported([]);
    setProgress(null);
    setAssignedConnectorIds([]);
    if (connectors.length === 0 && typeof loadConnectors === 'function') {
      loadConnectors({});
    }
    let cancelled = false;
    if (agentId) {
      getAssignedConnectorIds(agentId)
        .then((res) => {
          if (cancelled) return;
          const ids = Array.isArray(res?.data) ? res.data
                    : Array.isArray(res?.data?.connectorIds) ? res.data.connectorIds
                    : Array.isArray(res) ? res : [];
          setAssignedConnectorIds(ids);
        })
        .catch(() => { if (!cancelled) setAssignedConnectorIds([]); });
    }
    return () => { cancelled = true; };
  }, [isOpen, agentId, connectors.length, loadConnectors]);

  /* RBAC scoping — only connectors ASSIGNED to this agent are importable.
   * Mirrors SkillCreateSlideout.availableConnectors (agent-scoped). */
  const assignableConnectors = useMemo(() => {
    if (!assignedConnectorIds.length) return [];
    const idSet = new Set(assignedConnectorIds.map(String));
    return connectors.filter((c) => idSet.has(String(c.id)));
  }, [connectors, assignedConnectorIds]);

  /* Filter the agent-scoped connectors by free-text search. */
  const filteredConnectors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assignableConnectors;
    return assignableConnectors.filter((c) =>
      (c.name || '').toLowerCase().includes(q)
      || (c.type || '').toLowerCase().includes(q)
      || (c.description || '').toLowerCase().includes(q));
  }, [assignableConnectors, search]);

  /* Resolve the manifest for the currently-selected connector so we can
   * render its operations as importable skills. */
  const selectedConnector = useMemo(
    () => assignableConnectors.find((c) => c.id === selectedConnectorId) || null,
    [assignableConnectors, selectedConnectorId]
  );

  const selectedManifest = useMemo(() => {
    if (!selectedConnector || marketplaceConnectors.length === 0) return null;
    /* Same matcher + same (marketplace-shaped) input V1 used. */
    return findMarketplaceForInstalled(marketplaceConnectors, selectedConnector);
  }, [selectedConnector, marketplaceConnectors]);

  const availableSkills = useMemo(() => {
    if (!selectedManifest) return [];
    /* selectedManifest is the marketplace-shaped connector. Its `skills` are
     * already mapped by manifestToMarketplaceConnector with method / endpoint
     * / params / requiredScopes / samplePayload / credentialType /
     * accessDescription resolved from the matching operation — exactly the
     * shape V1's Import-from-Connector consumed (AgentsPage.jsx:819-855). */
    return Array.isArray(selectedManifest.skills) ? selectedManifest.skills : [];
  }, [selectedManifest]);

  const toggleSkill = (skillId) => {
    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  };

  const selectAll = () => setSelectedSkillIds(new Set(availableSkills.map((s) => s.id || s.name)));
  const selectNone = () => setSelectedSkillIds(new Set());

  const handleImport = async () => {
    if (!selectedConnector || selectedSkillIds.size === 0) return;
    setImporting(true);
    setError(null);
    setImported([]);
    const results = [];

    try {
      /* Step 1 — assign the connector to the agent (idempotent). */
      setProgress({ stage: 'assign', message: 'Assigning connector to agent…' });
      const assignRes = await apiFetch(
        `/api/v1/agents/${agentId}/connector-assignments`,
        'POST',
        { connectorId: selectedConnector.id }
      );
      /* 409 = already assigned → continue. */
      if (assignRes?.ok === false && assignRes?.status !== 409 && !assignRes?.data?.duplicate) {
        setError(`Failed to assign connector: ${assignRes?.error || 'Unknown error'}`);
        setImporting(false);
        return;
      }

      /* Step 2 — for each selected skill, create the Skill + link to connector. */
      const skillList = availableSkills.filter((s) => selectedSkillIds.has(s.id || s.name));
      for (let i = 0; i < skillList.length; i++) {
        const op = skillList[i];
        const skillId = op.id || op.name;
        const displayName = op.displayName || op.name || skillId;
        setProgress({ stage: 'create', message: `Creating skill ${i + 1}/${skillList.length}: ${displayName}…` });

        /* Build the Skill payload from the MAPPED marketplace skill — same
         * field set and values V1 sent (AgentsPage.jsx:838-855). The mapped
         * skill already carries endpoint/method/params/requiredScopes/
         * samplePayload/credentialType/accessDescription resolved from the
         * matching operation, so ConnectorExecutionService has everything it
         * needs to invoke at runtime. */
        const endpoint = op.endpoint || op.path || '';
        const method = (op.method || 'GET').toUpperCase();
        const tier = selectedManifest?.tier;
        const payload = {
          name: op.name || displayName,
          description: `${op.description || displayName} [${method} ${endpoint}]`,
          category: 'connectors',
          riskLevel: op.riskLevel || (tier === 'premium' ? 'medium' : 'low'),
          /* Canonical enum values — V2 previously sent the non-canonical
           * 'human_review' / 'platform', which the autonomy gates + display
           * surfaces don't recognise. */
          autonomy: 'HUMAN_CONTROLLED',
          inferenceMode: 'PLATFORM_INFERENCE',
          guardrails: [],
          skillType: 'connector_skill',
          endpoint: endpoint || null,
          httpMethod: method,
          baseUrl: selectedManifest?.baseUrl || selectedConnector.url || null,
          params: op.params || null,
          credentialType: op.credentialType || selectedManifest?.authType || selectedConnector.authMethod || null,
          requiredScopes: op.requiredScopes || null,
          samplePayload: op.samplePayload || null,
          accessDescription: op.accessDescription || `Calls ${method} ${endpoint} on ${selectedConnector.name}`,
        };

        const createRes = await apiFetch(`/api/v1/agents/${agentId}/skills`, 'POST', payload);
        if (!createRes?.ok || !createRes?.data?.id) {
          results.push({ skillName: displayName, ok: false, error: createRes?.error || 'Create failed' });
          setImported([...results]);
          continue;
        }
        const newSkillId = createRes.data.id;

        /* Step 3 — link the new skill to the connector. */
        const linkRes = await apiFetch(
          `/api/v1/agents/${agentId}/skills/${newSkillId}/connectors`,
          'POST',
          { connectorId: selectedConnector.id, purpose: 'data_source' }
        );
        if (!linkRes || linkRes.ok === false) {
          results.push({ skillName: displayName, ok: false, error: linkRes?.error || 'Link failed (skill created but not linked)' });
        } else {
          results.push({ skillName: displayName, ok: true });
        }
        setImported([...results]);

        /* Step 4 — seed an eval suite (best-effort, mirrors V1) so the
         * imported skill can pass the Build → Decide gate without a manual
         * round-trip. Failure here is non-fatal to the import. */
        if (newSkillId) {
          await generateEval(agentId, { skillId: newSkillId }).catch(() => null);
        }
      }

      setProgress({ stage: 'done', message: `Imported ${results.filter(r => r.ok).length} of ${results.length} skills.` });
      onImported && onImported();
    } catch (e) {
      setError(e?.message || 'Network error during import');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const allDone = imported.length > 0 && imported.length === selectedSkillIds.size && !importing;
  const successCount = imported.filter((r) => r.ok).length;
  const failureCount = imported.filter((r) => !r.ok).length;

  return (
    <Slideout
      isOpen={isOpen}
      onClose={importing ? undefined : onClose}
      width={820}
      ariaLabel={`Import skills from connector — ${agentName || 'agent'}`}
      header={
        <div className="flex items-center gap-2">
          <Plug size={16} className="text-v2-cyan" />
          <h2 className="text-[15px] font-bold m-0">Import skills from connector</h2>
          {agentName && <span className="text-v2-ink-muted font-normal text-[13px]">— {agentName}</span>}
        </div>
      }
      footer={
        allDone ? (
          <div className="flex items-center justify-end w-full">
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2 w-full">
            <Button variant="ghost" onClick={onClose} disabled={importing}>Cancel</Button>
            <Button
              variant="primary"
              iconLeft={<CheckCircle2 size={13} />}
              onClick={handleImport}
              loading={importing}
              disabled={!selectedConnector || selectedSkillIds.size === 0}
            >
              {importing
                ? (progress?.message || 'Importing…')
                : `Import ${selectedSkillIds.size || ''} ${selectedSkillIds.size === 1 ? 'skill' : 'skills'}`}
            </Button>
          </div>
        )
      }
    >
      {loading ? (
        <div className="text-[12.5px] text-v2-ink-muted">Loading installed connectors…</div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft p-3">
          <AlertCircle size={14} className="text-v2-danger flex-shrink-0 mt-0.5" />
          <div className="text-[12px] text-v2-danger">{error}</div>
        </div>
      ) : assignableConnectors.length === 0 ? (
        <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft px-3 py-3 text-[12px] text-v2-warning-strong flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">No connectors assigned to this agent.</div>
            <div className="mt-0.5">Grant connector access in <em>Team → Assignments → Agent assignments → Manage access</em> first, then return here to import its skills.</div>
          </div>
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: '220px 1fr' }}>
          {/* Connector list */}
          <div className="border border-v2-border-subtle rounded-v2-sm overflow-hidden flex flex-col" style={{ maxHeight: '420px' }}>
            <div className="relative border-b border-v2-border-subtle">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-v2-ink-muted" />
              <input
                type="text"
                placeholder="Search connectors"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-[11.5px] pl-7 pr-2 py-2 bg-v2-surface focus:outline-none v2-focus-ring"
                disabled={importing}
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredConnectors.length === 0 ? (
                <div className="p-3 text-[11.5px] text-v2-ink-muted">No matches.</div>
              ) : filteredConnectors.map((c) => {
                const sel = c.id === selectedConnectorId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setSelectedConnectorId(c.id); setSelectedSkillIds(new Set()); }}
                    disabled={importing}
                    className={
                      'w-full text-left px-2.5 py-2 border-b border-v2-border-subtle last:border-b-0 ' +
                      'text-[12px] transition-colors duration-v2 ' +
                      (sel ? 'bg-v2-cyan-soft text-v2-ink' : 'bg-v2-surface text-v2-ink-secondary hover:bg-v2-muted')
                    }
                  >
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-[10.5px] text-v2-ink-muted truncate">{c.type || c.authMethod || '—'}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Skill list for selected connector */}
          <div className="border border-v2-border-subtle rounded-v2-sm overflow-hidden flex flex-col" style={{ maxHeight: '420px' }}>
            {!selectedConnector ? (
              <div className="flex-1 flex items-center justify-center text-[12px] text-v2-ink-muted p-4 text-center">
                Pick a connector to see its available skills.
              </div>
            ) : !selectedManifest ? (
              <div className="flex-1 flex items-center justify-center text-[12px] text-v2-ink-muted p-4 text-center">
                No manifest found for <span className="font-semibold mx-1">{selectedConnector.name}</span> — this connector predates the registry. You can still author skills manually with "New skill".
              </div>
            ) : availableSkills.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-[12px] text-v2-ink-muted p-4 text-center">
                Manifest declares no importable operations.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-v2-border-subtle bg-v2-muted/30">
                  <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                    {availableSkills.length} skills · {selectedSkillIds.size} selected
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <button type="button" onClick={selectAll} className="text-[10.5px] text-v2-trust hover:underline" disabled={importing}>All</button>
                    <span className="text-v2-ink-muted text-[10px]">·</span>
                    <button type="button" onClick={selectNone} className="text-[10.5px] text-v2-trust hover:underline" disabled={importing}>None</button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {availableSkills.map((s) => {
                    const sid = s.id || s.name;
                    const checked = selectedSkillIds.has(sid);
                    const importedRow = imported.find((r) => r.skillName === (s.displayName || s.name || sid));
                    return (
                      <label
                        key={sid}
                        className={
                          'flex items-start gap-2 px-3 py-2 border-b border-v2-border-subtle last:border-b-0 cursor-pointer ' +
                          (checked ? 'bg-v2-cyan-soft/30' : 'hover:bg-v2-muted')
                        }
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={checked}
                          onChange={() => toggleSkill(sid)}
                          disabled={importing}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] font-medium text-v2-ink truncate">{s.displayName || s.name || sid}</span>
                            {s.method && <Pill tone="muted" size="sm">{s.method}</Pill>}
                            {importedRow && (
                              importedRow.ok
                                ? <Pill tone="success" size="sm">imported</Pill>
                                : <Pill tone="danger" size="sm" title={importedRow.error}>failed</Pill>
                            )}
                          </div>
                          <div className="text-[10.5px] text-v2-ink-muted mt-0.5 line-clamp-2">{s.description || s.endpoint || '—'}</div>
                          {s.endpoint && (
                            <div className="text-[10px] text-v2-ink-muted font-mono-v2 mt-1 truncate">{s.endpoint}</div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {progress && (
        <div className="mt-3 text-[11.5px] text-v2-ink-secondary">{progress.message}</div>
      )}
      {allDone && (
        <div className={
          'mt-3 rounded-v2-sm border p-3 ' +
          (failureCount === 0
            ? 'border-v2-success-border bg-v2-success-soft'
            : 'border-v2-warning-border bg-v2-warning-soft')
        }>
          <div className={'text-[12px] font-semibold ' + (failureCount === 0 ? 'text-v2-success' : 'text-v2-warning')}>
            {successCount === imported.length
              ? `Successfully imported ${successCount} ${successCount === 1 ? 'skill' : 'skills'}.`
              : `Imported ${successCount} of ${imported.length} — ${failureCount} failed.`}
          </div>
          {failureCount > 0 && (
            <ul className="mt-1.5 space-y-1 text-[11px] text-v2-ink-secondary">
              {imported.filter((r) => !r.ok).map((r, i) => (
                <li key={i}><span className="font-mono-v2">{r.skillName}</span>: {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Slideout>
  );
}

export default ImportFromConnectorModalV2;
